import React from "react";
import styled from "styled-components";
import theme from "theme";
import {
	MdCloud,
	MdCollectionsBookmark,
	MdComputer,
	MdDeveloperBoard,
	MdDns,
	MdEmail,
	MdRouter,
	MdSettingsEthernet,
	MdShowChart,
	MdTimeline,
	MdVpnLock,
} from "react-icons/md";
import { SiDiscord, SiMicrosoftteams, SiSlack } from "react-icons/si";
import { integrationsRow, sectionIds } from "content/site";
import { Container, Section, SectionTitle, SectionLead, MarketingCard, TextLink } from "components/marketing/primitives";

const ICON_PX = 28;

const integrationMeta = {
	slack: {
		Icon: SiSlack,
		variant: "brand",
		brandBg: "#4a154b",
		brandColor: "#ffffff",
	},
	discord: {
		Icon: SiDiscord,
		variant: "brand",
		brandBg: "#5865f2",
		brandColor: "#ffffff",
	},
	teams: {
		Icon: SiMicrosoftteams,
		variant: "brand",
		brandBg: "#5558af",
		brandColor: "#ffffff",
	},
	siem: {
		Icon: MdShowChart,
		variant: "symbol",
	},
	edr: {
		Icon: MdComputer,
		variant: "symbol",
	},
	ngfw: {
		Icon: MdRouter,
		variant: "symbol",
	},
	ctiPlatforms: {
		Icon: MdCollectionsBookmark,
		variant: "symbol",
	},
	emailSecOps: {
		Icon: MdEmail,
		variant: "symbol",
	},
	cloudSecPlatform: {
		Icon: MdCloud,
		variant: "symbol",
	},
	dns: {
		Icon: MdDns,
		variant: "symbol",
	},
	soar: {
		Icon: MdTimeline,
		variant: "symbol",
	},
	casb: {
		Icon: MdVpnLock,
		variant: "symbol",
	},
	sase: {
		Icon: MdSettingsEthernet,
		variant: "symbol",
	},
	otIcs: {
		Icon: MdDeveloperBoard,
		variant: "symbol",
	},
};

const Grid = styled.ul`
	list-style: none;
	margin: 0;
	padding: 0;
	display: grid;
	grid-template-columns: ${(p) => {
		if (p.$fixedCols && p.$compact) return `repeat(${p.$fixedCols}, minmax(0, 6.25rem))`;
		if (p.$fixedCols) return `repeat(${p.$fixedCols}, minmax(0, 1fr))`;
		return "repeat(auto-fill, minmax(9.5rem, 1fr))";
	}};
	gap: ${(p) => (p.$compact ? "0.65rem 0.85rem" : "1.15rem")};
	justify-content: ${(p) => (p.$compact ? "start" : "stretch")};

	${(p) =>
		p.$fixedCols
			? `
		@media (max-width: 1024px) {
			grid-template-columns: repeat(3, minmax(0, ${p.$compact ? "6.25rem" : "1fr"}));
		}
		@media (max-width: 760px) {
			grid-template-columns: repeat(2, minmax(0, ${p.$compact ? "6rem" : "1fr"}));
		}
		@media (max-width: 480px) {
			grid-template-columns: repeat(2, minmax(0, ${p.$compact ? "5.5rem" : "1fr"}));
		}
	`
			: ""}
`;

const Group = styled.div`
	margin-top: 1.45rem;
`;

const GroupTitle = styled.h3`
	font-family: ${theme.fontFamily.sans};
	font-size: 1.0625rem;
	font-weight: 800;
	margin: 0 0 0.4rem;
	color: ${theme.color.dark};
	letter-spacing: -0.02em;
`;

const GroupDescription = styled.p`
	font-family: ${theme.fontFamily.sans};
	font-size: 0.9375rem;
	color: ${theme.color.greyD2};
	margin: 0 0 0.9rem;
	line-height: 1.55;
	max-width: 48rem;
`;

const GroupLinks = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 0.9rem 1.1rem;
	margin: 0.95rem 0 0;
`;

const Card = styled(MarketingCard).attrs({ as: "li" })`
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	gap: 0.7rem;
	padding: ${(p) => (p.$minimal ? "0.15rem 0.05rem" : "1.15rem 0.75rem")};
	min-height: auto;

	${(p) =>
		p.$minimal
			? `
		background: transparent;
		border: none;
		box-shadow: none;
		transform: none;
		overflow: visible;
		&::before {
			display: none;
		}
		&:hover {
			border-color: transparent;
			box-shadow: none;
			transform: none;
		}
	`
			: ""}
`;

const IconWrap = styled.div`
	width: ${(p) => (p.$compact ? "2.55rem" : "3.35rem")};
	height: ${(p) => (p.$compact ? "2.55rem" : "3.35rem")};
	border-radius: ${theme.radius.md};
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	background: ${(p) => (p.$variant === "brand" ? p.$brandBg : theme.color.primaryMuted)};
	color: ${(p) => (p.$variant === "brand" ? p.$brandColor : theme.color.primary)};
	border: 1px solid ${(p) => (p.$variant === "brand" ? "transparent" : "rgba(0, 119, 204, 0.15)")};
`;

const Label = styled.span`
	font-family: ${theme.fontFamily.sans};
	font-size: 0.8125rem;
	font-weight: 700;
	color: ${theme.color.darkL2};
	line-height: 1.3;
`;

const Note = styled.p`
	font-family: ${theme.fontFamily.sans};
	font-size: 0.9375rem;
	color: ${theme.color.greyD2};
	line-height: 1.55;
	max-width: 48rem;
	margin: 1.35rem 0 0;
`;

export default function IntegrationsRow() {
	return (
		<Section id={sectionIds.integrations} aria-labelledby="integrations-heading">
			<Container>
				<SectionTitle id="integrations-heading">{integrationsRow.title}</SectionTitle>
				<SectionLead>{integrationsRow.lead}</SectionLead>
				{integrationsRow.groups.map((group) => (
					<Group key={group.key}>
						<GroupTitle>{group.title}</GroupTitle>
						<GroupDescription>{group.description}</GroupDescription>
						<Grid
							aria-label={group.title}
							$fixedCols={group.key === "apiEnabled" ? 5 : undefined}
							$compact={group.key === "apiEnabled"}
						>
							{group.destinations.map((d) => {
								const meta = integrationMeta[d.key];
								if (!meta) return null;
								const { Icon, variant, brandBg, brandColor } = meta;
								return (
									<Card key={`${group.key}-${d.key}`} $minimal>
										<IconWrap
											$variant={variant}
											$brandBg={brandBg}
											$brandColor={brandColor}
											$compact={group.key === "apiEnabled"}
										>
											<Icon size={group.key === "apiEnabled" ? 22 : ICON_PX} aria-hidden focusable="false" />
										</IconWrap>
										<Label>{d.label}</Label>
									</Card>
								);
							})}
						</Grid>
						{group.links?.length ? (
							<GroupLinks>
								{group.links.map((link) => (
									<TextLink key={link.href} href={link.href}>
										{link.label} →
									</TextLink>
								))}
							</GroupLinks>
						) : null}
					</Group>
				))}
				<Note>{integrationsRow.note}</Note>
			</Container>
		</Section>
	);
}
